import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load cart from localStorage on init
    const savedCart = localStorage.getItem('abaya-cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('abaya-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, selectedSize, selectedColor, selectedLength, quantity = 1, notes = '') => {
    // SECURITY: Validate quantity (must be positive integer between 1 and 100)
    const qty = Number(quantity);
    if (!Number.isInteger(qty) || qty < 1 || qty > 100) {
      throw new Error('Quantity must be between 1 and 100');
    }

    // SECURITY: Validate price (must be positive)
    const price = Number(product.price);
    if (isNaN(price) || price <= 0 || price > 1000000) {
      throw new Error('Invalid product price');
    }

    // SECURITY: Validate notes (max 500 characters)
    const sanitizedNotes = typeof notes === 'string' ? notes.trim().substring(0, 500) : '';
    const normalizedLength = selectedLength ? String(selectedLength) : '';
    const normalizedColor = selectedColor ? String(selectedColor) : '';

    setCartItems((prevItems) => {
      // Check if item with same product and size already exists
      const existingItemIndex = prevItems.findIndex((item) => {
        const itemColor = item.selectedColor ? String(item.selectedColor) : '';
        const itemLength = item.selectedLength ? String(item.selectedLength) : '';
        return (
          item.id === product.id &&
          item.selectedSize === selectedSize &&
          itemLength === normalizedLength &&
          itemColor === normalizedColor
        );
      });

      if (existingItemIndex > -1) {
        // Update quantity of existing item (with limit)
        const updatedItems = [...prevItems];
        const newQuantity = updatedItems[existingItemIndex].quantity + qty;
        if (newQuantity > 100) {
          throw new Error('Maximum quantity per item is 100');
        }
        updatedItems[existingItemIndex].quantity = newQuantity;
        // Update notes if provided
        if (sanitizedNotes) {
          updatedItems[existingItemIndex].notes = sanitizedNotes;
        }
        return updatedItems;
      } else {
        // Add new item
        return [
          ...prevItems,
          {
            id: product.id,
            name: product.name,
            price: price, // Use validated price
            image: product.image,
            selectedSize,
            selectedColor: normalizedColor,
            selectedLength: normalizedLength,
            quantity: qty, // Use validated quantity
            badge: product.badge,
            notes: sanitizedNotes,
          },
        ];
      }
    });
  };

  const removeFromCart = (itemId, selectedSize, selectedColor = '', selectedLength = '') => {
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) => {
          const itemColor = item.selectedColor ? String(item.selectedColor) : '';
          const itemLength = item.selectedLength ? String(item.selectedLength) : '';
          const matches =
            item.id === itemId &&
            item.selectedSize === selectedSize &&
            itemLength === String(selectedLength || '') &&
            itemColor === String(selectedColor || '');
          return !matches;
        }
      )
    );
  };

  const updateQuantity = (itemId, selectedSize, selectedColor = '', selectedLength = '', newQuantity) => {
    // SECURITY: Validate quantity
    const qty = Number(newQuantity);
    if (!Number.isInteger(qty) || qty < 1 || qty > 100) {
      if (qty < 1) {
        removeFromCart(itemId, selectedSize, selectedColor, selectedLength);
        return;
      }
      throw new Error('Quantity must be between 1 and 100');
    }

    setCartItems((prevItems) =>
      prevItems.map((item) => {
        const itemColor = item.selectedColor ? String(item.selectedColor) : '';
        const itemLength = item.selectedLength ? String(item.selectedLength) : '';
        const matches =
          item.id === itemId &&
          item.selectedSize === selectedSize &&
          itemLength === String(selectedLength || '') &&
          itemColor === String(selectedColor || '');
        return matches ? { ...item, quantity: qty } : item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
